import { useEffect, useState } from "react";

import { logError } from "../../../utils/appUtils";
import { GateCheckInStatus } from "../../../utils/constants";
import {
  isBoundByQueueOrder,
  nextInGateQueue,
} from "../../../utils/gateQueueOrder";
import { fetchWaitingGateCheckIns } from "./gateCheckInUtils";

const { ARRIVED } = GateCheckInStatus;

/**
 * Whether this check-in is the truck a supervisor may call next.
 *
 * The Gate Queue pages know this already — they hold the whole queue. The
 * shipment dispatch tabs hold one check-in and no queue at all, which is how a
 * supervisor could call a truck forward there that the queue page would not
 * let them call. This reads the waiting rows for the tab.
 *
 * Read once per row rather than polled: the answer only greys out menu items,
 * and saveGateCheckIn re-tests the order against the live queue before it
 * writes, so a stale yes here costs a refused save, not an out-of-turn call.
 * A read that fails leaves the menu open for the same reason.
 *
 * Nothing is read at all unless the answer could matter: a manager is not
 * bound by the order, and a row already CALLED or DOCKED is through the gate.
 *
 * @returns {{orderBound: boolean, isNextInQueue: boolean, nextLabel: string|null}}
 */
export const useGateQueueTurn = (checkIn) => {
  const orderBound = isBoundByQueueOrder(window.appUser?.type);
  const [turn, setTurn] = useState({ isNextInQueue: true, nextLabel: null });
  const checkInId = checkIn?.id;
  const yardId = checkIn?.reloadYardId || window.appUser?.reloadYardId;
  const ordered = orderBound && checkIn?.status === ARRIVED;

  useEffect(() => {
    if (!ordered || !checkInId || !yardId) {
      setTurn({ isNextInQueue: true, nextLabel: null });
      return undefined;
    }
    let cancelled = false;
    fetchWaitingGateCheckIns(yardId)
      .then((waiting) => {
        if (cancelled) return;
        const next = nextInGateQueue(waiting, (c) => c.status);
        setTurn({
          // A queue that does not list this row cannot say it is out of turn.
          isNextInQueue: !next || next.id === checkInId,
          nextLabel: next?.driverName || next?.carrierName || null,
        });
      })
      .catch((err) => {
        if (!cancelled) logError("useGateQueueTurn:fetch", err, false);
      });
    return () => {
      cancelled = true;
    };
  }, [ordered, checkInId, yardId]);

  return { orderBound, ...turn };
};
